/**
 * Etkinlik ücret/bilet bilgisini CMS alanlarından tek yerden türetir.
 *
 * Strapi "event" modelinde üç ayrı alan var ve tek başına `price` yeterli değil:
 *   - price_type : 'ücretli' | 'ücretsiz' | null
 *   - ticket     : 'biletli' | 'biletsiz' | (nadiren bilet linki URL) | null
 *   - price      : sayı (ücretli etkinliklerde çoğunlukla 0 bırakılıyor)
 *
 * Eski mantık yalnızca `price === 0` → "Ücretsiz" diyordu; bu yüzden CMS'te
 * "ücretli + biletli" işaretlenen etkinlikler takvimde ücretsiz görünüyordu.
 */

const norm = (v) => (typeof v === 'string' ? v.trim().toLocaleLowerCase('tr-TR') : '')

const toNumber = (v) => {
  if (v === null || v === undefined || v === '') return NaN
  if (typeof v === 'number') return Number.isFinite(v) ? v : NaN
  const n = parseFloat(String(v).replace(',', '.').replace(/[^\d.]/g, ''))
  return Number.isFinite(n) ? n : NaN
}

const isHttpUrl = (s) => typeof s === 'string' && /^https?:\/\/\S+/i.test(s.trim())

export const formatPrice = (n) =>
  `${Number(n).toLocaleString('tr-TR', { maximumFractionDigits: 2 })} ₺`

/**
 * @param {object} ev  Ham CMS kaydı ya da normalize edilmiş etkinlik
 *                     (price, price_type | priceType, ticket alanları okunur)
 * @returns {{
 *   isPaid: boolean, isFree: boolean, isTicketed: boolean,
 *   price: number|null, label: string|null,
 *   ticketLabel: string|null, ticketUrl: string|null
 * }}
 */
export function getEventPricing(ev = {}) {
  const priceType = norm(ev.price_type ?? ev.priceType)
  const ticketRaw = ev.ticket
  const ticket = norm(ticketRaw)
  const priceNum = toNumber(ev.price)
  const hasPrice = priceNum > 0

  const isPaid = priceType === 'ücretli' || priceType === 'ucretli' || hasPrice
  const isFree = !isPaid && (priceType === 'ücretsiz' || priceType === 'ucretsiz' || priceNum === 0)

  const ticketUrl = isHttpUrl(ticketRaw) ? ticketRaw.trim() : null
  const isTicketed = ticket === 'biletli' || !!ticketUrl

  let label = null
  if (isPaid) label = hasPrice ? formatPrice(priceNum) : 'Ücretli'
  else if (isFree) label = 'Ücretsiz'

  let ticketLabel = null
  if (isTicketed) ticketLabel = 'Biletli'
  else if (ticket === 'biletsiz') ticketLabel = 'Biletsiz'

  return {
    isPaid,
    isFree,
    isTicketed,
    price: Number.isFinite(priceNum) ? priceNum : null,
    label,
    ticketLabel,
    ticketUrl,
  }
}
